/* eslint-disable camelcase */
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector, useStore} from 'react-redux';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from '../../../../../../../../web/src/utils/SnackBarConfig';
import {getAllPatientAppointments} from './../../../../../../../../shared/src/services/AppointmentsServices';
import AppointmentCardDetails from '../../../Appointments/AppointmentCardDetails';
import {Typography, makeStyles} from '@material-ui/core';
import WithPermission from '@components/system/WithPermission/WithPermission';
import {AppPermissions, EligibilityLegends} from '@shared/constants/Mapping';
import {getClinicGroupsDetails} from '@shared/services/ClinicServices';
import Loader from '@components/framework/loader/Loader';

const useStyles = makeStyles({
  noAppointment: {
    textAlign: 'center',
    fontWeight: 600
  },
  sectionRow: {
    display: 'grid',
    gridTemplateColumns: '100%',
    fontSize: '1.3rem',
    paddingBottom: '1rem'
  },
  DrawerInfo: {
    fontSize: '1.3rem',
    fontWeight: 600
  },
  appointmentParent: {
    paddingTop: '1rem',
    position: 'relative',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    overflow: 'hidden'
  },
  eligibilityStatusLegendWrapper: {
    display: 'flex',
    columnGap: '2rem',
    rowGap: '1rem',
    flexWrap: 'wrap',
    marginBottom: '1rem'
  },
  eligibilityStatusLegend: {
    display: 'flex',
    gap: '0.75rem'
  },
  eligibilityStatusLegendBox: {
    width: '1rem',
    height: '1rem'
  },
  appointmentsWrapper: {
    flex: 1,
    overflow: 'auto'
  }
});
const PatientAppointmentCardDetails = (): JSX.Element => {
  const classes = useStyles();

  const {enqueueSnackbar} = useSnackbar();
  const [groupDetails, setGroupDetails]: any = useState({});

  const patientDetailsDrawer = useSelector(
    (state: any) => state.patientDetailsDrawer ?? {}
  );

  const store = useStore();

  const clinics = useSelector((state: any) => state.clinics.list);

  const {t} = useTranslation();

  const [appointmentHistory, setAppointmentsHistory]: any = useState([]);
  const [loadingState, setLoadingState] = useState({
    loadingAppointmentHistory: true,
    loadingGroupDetails: true
  });

  const loadAppointmentHistory = async (): Promise<void> => {
    const drawer = store.getState().patientDetailsDrawer ?? {};
    const focusRaw = drawer.focusedAppointmentId;
    const focusId =
      focusRaw != null && focusRaw !== ''
        ? Number(focusRaw)
        : null;
    const useSingleAppointment =
      focusId != null && Number.isFinite(focusId) && focusId > 0;

    try {
      const response = await getAllPatientAppointments(
        drawer.groupId,
        drawer.clinicId,
        drawer.patientId,
        useSingleAppointment ? {appointment_id: focusId} : undefined
      );
      if (response?.data?.rows?.length > 0) {
        setAppointmentsHistory(response?.data?.rows);
      } else {
        setAppointmentsHistory([]);
      }
    } catch (error: any) {
      enqueueSnackbar(
        (error && error.message) ||
          t('PATIENTS.GET_PATIENT_APPOINTMENT_DETAILS_ERROR'),
        SnackBarConfig('e')
      );
      setAppointmentsHistory([]);
    } finally {
      setLoadingState(p => ({...p, loadingAppointmentHistory: false}));
    }
  };

  const getSelectedGroupDetails = async (id: any): Promise<void> => {
    try {
      getClinicGroupsDetails(id)
        .then((clinicGroupDetails: any) => {
          if (clinicGroupDetails?.data) {
            setGroupDetails(clinicGroupDetails.data);
          }
        })
        .catch(error => {
          enqueueSnackbar(
            t('CLINICS.CLINIC_GROUP_DETAILS_ERROR'),
            SnackBarConfig('e')
          );
          throw error;
        });
    } catch (error: any) {
      enqueueSnackbar(
        (error && error.message) || t('CLINICS.CLINIC_GROUP_DETAILS_ERROR'),
        SnackBarConfig('e')
      );
      throw error;
    } finally {
      setLoadingState(p => ({...p, loadingGroupDetails: false}));
    }
  };

  React.useEffect(() => {
    if (patientDetailsDrawer?.groupId) {
      getSelectedGroupDetails(patientDetailsDrawer.groupId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientDetailsDrawer?.groupId]);

  React.useEffect(() => {
    if (!patientDetailsDrawer?.patientId) {
      return;
    }
    setLoadingState(p => ({...p, loadingAppointmentHistory: true}));
    loadAppointmentHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    store,
    patientDetailsDrawer?.patientId,
    patientDetailsDrawer?.clinicId,
    patientDetailsDrawer?.groupId,
    patientDetailsDrawer?.focusedAppointmentId
  ]);

  const updateAppointmentHistoryArray = async (
    updatedAppointmentObj: any
  ): Promise<void> => {
    const updatedAppointments = appointmentHistory.map((d: any) => {
      if (d?.id === updatedAppointmentObj?.id) {
        return updatedAppointmentObj;
      }
      return d;
    });
    setAppointmentsHistory(updatedAppointments);
  };

  // clinicId to clinicDetails Mapper
  const clinicIdToDetailsMapper: Record<string, any> = {};
  if (Array.isArray(clinics)) {
    for (const clinic of clinics) {
      clinicIdToDetailsMapper[clinic.id] = clinic;
    }
  }

  const renderAppointments = () => {
    return (
      <div className={classes.appointmentsWrapper}>
        {appointmentHistory.map((item: any, index: number) => (
          <AppointmentCardDetails
            key={index}
            appointmentObject={item}
            updateAppointmentArray={updateAppointmentHistoryArray}
            isPatientNameALink={false}
            showDateTime={true}
            showLocation={true}
            location={clinicIdToDetailsMapper[item.clinic_id]?.name ?? ''}
            groupDetails={groupDetails}
          />
        ))}
      </div>
    );
  };
  const renderNoAppointmentInfo = () => {
    return (
      <div className={classes.noAppointment}>
        {t('APPOINTMENTS.NO_APPOINTMENT_DATA')}
      </div>
    );
  };
  return (
    <div className={classes.appointmentParent}>
      <Typography
        className={[classes.sectionRow, classes.DrawerInfo].join(' ')}>
        {t('APPOINTMENTS.APPOINTMENT_DETAILS')}
      </Typography>
      {loadingState.loadingAppointmentHistory ||
      loadingState.loadingGroupDetails ? (
        <Loader />
      ) : (
        <>
          {appointmentHistory?.length > 0 && (
            <div className={classes.eligibilityStatusLegendWrapper}>
              {EligibilityLegends.map(item => (
                <div
                  key={item.name}
                  className={classes.eligibilityStatusLegend}>
                  <div
                    className={classes.eligibilityStatusLegendBox}
                    style={{
                      backgroundColor: item.backgroundColor
                    }}
                  />
                  <p>{t(item.name)}</p>
                </div>
              ))}
            </div>
          )}
          {appointmentHistory?.length > 0
            ? renderAppointments()
            : renderNoAppointmentInfo()}
        </>
      )}
    </div>
  );
};

export default WithPermission(
  PatientAppointmentCardDetails,
  AppPermissions.APPOINTMENT.NAME,
  AppPermissions.APPOINTMENT.LIST
);
